import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { PPNewDetailLink } from '@/modules/shared/components/ppnew-detail-link/PPNewDetailLink'
import { Tooltip } from '@/modules/shared/components/tooltip/Tooltip'
import { useViewType } from '@/modules/shared/hooks/useViewType'
import { CheckIcon } from '@/modules/shared/icons/CheckIcon'
import { classNames } from '@/modules/shared/utils/classNames'

interface PreferredSupplierProps {
  preferredSupplier: boolean
  supplierName?: string | null
  supplierRelationShipId?: number | null
  supplierId?: number | null
  className?: string
  linkType?: 'supplier' | 'supplier-directory'
  plainText?: boolean
}

export default function PreferredSupplier({
  preferredSupplier,
  supplierName,
  className,
  supplierRelationShipId,
  supplierId,
  linkType = 'supplier',
  plainText = false,
}: PreferredSupplierProps) {
  const { t } = useTranslation()
  const { isUtility } = useViewType()

  // Memoize the tooltip content to avoid recreating it on every render
  const preferredSupplierTooltip = useMemo(
    () =>
      t(
        'shopPage.productList.productCard.preferredSupplierTooltip',
        'Your organisation has tagged this Supplier as a Preferred Supplier'
      ),
    [t]
  )

  // Extract the supplier display content to avoid duplication
  const supplierContent = (
    <div className={classNames('flex items-center gap-x-1', className)}>
      {supplierName}
      {preferredSupplier && (
        <Tooltip content={preferredSupplierTooltip}>
          <CheckIcon className="size-4" />
        </Tooltip>
      )}
    </div>
  )

  // Handle case where there's no supplier relationship
  if (!supplierRelationShipId) {
    return <span className={className}>{supplierName || t('general.noSuppliers', 'No Suppliers')}</span>
  }

  // Return plain text version without link
  if (plainText) {
    return supplierContent
  }

  // Return linked version
  return (
    <PPNewDetailLink
      type={isUtility ? 'utility-organisation' : linkType}
      id={String(isUtility ? supplierId : supplierRelationShipId)}
      openNewTab
    >
      {supplierContent}
    </PPNewDetailLink>
  )
}
